import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Dashboard from './Dashboard';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';
import './AlarmPopup.css';

/**
 * AlarmPopup 컴포넌트
 * 알람을 팝업 형태로 표시하고 스누즈/확인 기능을 제공합니다.
 */
const AlarmPopup = ({ 
  alarmKey, 
  dashboardId, 
  snoozeDuration = 300,
  popupSizeMode = 'fixed',
  popupWidth = null,
  popupHeight = null,
  popupPaddingVertical = 40,
  popupPaddingHorizontal = 40,
  transparentMode = false,  // 기본값: 투명 모드 비활성화 (신규 알람 생성 시에만 기본 true)
  onConfirm, 
  onSnooze 
}) => {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);
  const [computedSize, setComputedSize] = useState(null);
  
  const dashboardBodyRef = useRef(null);
  
  // 전역 변수 컨텍스트 (대시보드 내 사용자 입력 변수 접근)
  const globalVariables = useGlobalVariables();
  
  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);
    
    try {
      // 현재 대시보드 내 모든 변수 수집 (사용자 입력 포함)
      const popupVariables = globalVariables?.variables || {};
      
      console.log('[AlarmPopup] 팝업 변수 수집:', popupVariables);
      
      // 확인 함수에 alarmKey와 팝업 변수 모두 전달
      await onConfirm(alarmKey, popupVariables);
    } catch (err) {
      console.error('[AlarmPopup] 확인 처리 중 오류:', err);
      setError(err.message || '알람 확인 처리에 실패했습니다.');
    } finally {
      setConfirming(false);
    }
  };
  
  const handleSnooze = () => {
    onSnooze(alarmKey, snoozeDuration);
  };
  
  const formatSnoozeDuration = (seconds) => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      
      if (remainingSeconds === 0) {
        return `${minutes}분`;
      } else {
        return `${minutes}분 ${remainingSeconds}초`;
      }
    }
    return `${seconds}초`;
  };
  
  const handleBackdropClick = (e) => {
    e.stopPropagation();
  };
  
  // 대시보드 로드 완료 시 크기 계산 (자동 모드)
  const handleDashboardLoad = () => {
    setDashboardLoaded(true);
    
    if (popupSizeMode === 'auto' && dashboardBodyRef.current) {
      // 약간의 지연 후 크기 측정 (렌더링 완료 대기)
      setTimeout(() => {
        const bodyElement = dashboardBodyRef.current;
        if (bodyElement) {
          const contentRect = bodyElement.getBoundingClientRect();
          const scrollHeight = bodyElement.scrollHeight;
          const scrollWidth = bodyElement.scrollWidth;
          
          // 실제 콘텐츠 크기 + 여백
          const calculatedWidth = Math.max(scrollWidth, contentRect.width) + popupPaddingHorizontal * 2;
          const calculatedHeight = Math.max(scrollHeight, contentRect.height) + popupPaddingVertical * 2;
          
          // 화면 크기 제한 (최대 95%)
          const maxWidth = window.innerWidth * 0.95;
          const maxHeight = window.innerHeight * 0.95;
          
          const finalWidth = Math.min(calculatedWidth, maxWidth);
          const finalHeight = Math.min(calculatedHeight, maxHeight);
          
          console.log('[AlarmPopup] 자동 크기 계산:', {
            scroll: { width: scrollWidth, height: scrollHeight },
            content: { width: contentRect.width, height: contentRect.height },
            calculated: { width: calculatedWidth, height: calculatedHeight },
            final: { width: finalWidth, height: finalHeight }
          });
          
          setComputedSize({ width: finalWidth, height: finalHeight });
        }
      }, 300);
    }
  };
  
  // 컨테이너 스타일 계산
  const getContainerStyle = () => {
    // 최소 크기 설정 (버튼 영역이 항상 보이도록)
    const MIN_WIDTH = 500;
    const MIN_HEIGHT = 400;
    
    const baseStyle = {
      minWidth: `${MIN_WIDTH}px`,
      minHeight: `${MIN_HEIGHT}px`
    };
    
    if (popupSizeMode === 'manual' && popupWidth && popupHeight) {
      // 수동 모드: 지정된 크기 사용 (최소 크기 보장)
      baseStyle.width = `${Math.max(popupWidth, MIN_WIDTH)}px`;
      baseStyle.height = `${Math.max(popupHeight, MIN_HEIGHT)}px`;
      baseStyle.maxWidth = 'none';
      baseStyle.maxHeight = 'none';
    } else if (popupSizeMode === 'auto' && computedSize) {
      // 자동 모드: 계산된 크기 사용 (최소 크기 보장)
      baseStyle.width = `${Math.max(computedSize.width, MIN_WIDTH)}px`;
      baseStyle.height = `${Math.max(computedSize.height, MIN_HEIGHT)}px`;
      baseStyle.maxWidth = 'none';
      baseStyle.maxHeight = 'none';
    }
    // fixed 모드는 CSS 기본값 사용 (85%, max-width: 1000px)
    
    // 투명 모드: 배경, 테두리, 그림자 제거
    if (transparentMode) {
      baseStyle.background = 'transparent';
      baseStyle.border = 'none';
      baseStyle.boxShadow = 'none';
      baseStyle.borderRadius = '0';
    }
    
    return baseStyle;
  };
  
  return ReactDOM.createPortal(
    <div className="alarm-popup-backdrop" onClick={handleBackdropClick}>
      <div 
        className={`alarm-popup-container${transparentMode ? ' transparent-mode' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={getContainerStyle()}
      >
        <div 
          className="alarm-popup-body" 
          ref={dashboardBodyRef}
          style={transparentMode ? { background: 'transparent' } : undefined}
        >
          <Dashboard 
            dashboardId={dashboardId} 
            isPopup={true}
            transparentMode={transparentMode}
            initialVariables={{ alarm_key: alarmKey }}
            onLoad={handleDashboardLoad}
            onError={(err) => {
              console.error('[AlarmPopup] 대시보드 로드 오류:', err);
              setError('알람 대시보드를 불러오는 데 실패했습니다.');
            }}
          />
        </div>
        
        {error && (
          <div className="alarm-popup-error">
            <span className="alarm-error-icon">⚠️</span>
            <span className="alarm-error-message">{error}</span>
          </div>
        )}
        
        <div className="alarm-popup-footer">
          <button 
            className="alarm-snooze-btn"
            onClick={handleSnooze}
            disabled={confirming}
            title="알람을 일시적으로 숨기고 나중에 다시 표시합니다"
          >
            <span className="alarm-btn-icon">🔕</span>
            <span className="alarm-btn-text">
              닫기 ({formatSnoozeDuration(snoozeDuration)} 후 재알림)
            </span>
          </button>
          
          <button 
            className="alarm-confirm-btn"
            onClick={handleConfirm}
            disabled={confirming}
            title="알람을 확인하고 처리합니다"
          >
            <span className="alarm-btn-icon">{confirming ? '⏳' : '✓'}</span>
            <span className="alarm-btn-text">
              {confirming ? '처리 중...' : '확인'}
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.getElementById('modal-root') || document.body
  );
};

export default AlarmPopup;

